import { useCallback, useState } from "react";
import type { FlexProps } from "@chakra-ui/react";
import { Box, Button, Flex, Tab, TabList, TabPanel, TabPanels, useMediaQuery, useTheme } from "@chakra-ui/react";
import { FxBestWorstPerformancePanel } from "./FxBestWorstPerformancePanel";
import { FxPerformancePriipsScenariosPanel } from "./FxPerformancePriipsScenariosPanel";
import type { DataTable } from "@/types";
import { AccessibleSelect } from "@/components/AccessibleSelect/AccessibleSelect";
import { AccessibleTabs } from "@/components/AccessibleTabs/AccessibleTabs";
import { ContentBlock } from "@/components/ContentBlock/ContentBlock";
import { Disclaimer } from "@/components/Disclaimer/Disclaimer";
import { FundDividendInfoDrawer } from "@/components/FundDividendInfoDrawer/FundDividendInfoDrawer";
import { PerfomanceTable } from "@/components/PerfomanceTable/PerfomanceTable";
import { WithTooltip } from "@/components/WithTooltip/WithTooltip";
import { useInstanceConfig } from "@/services/InstanceConfigContext";
import { GET } from "@/services/api/client";
import { envVar } from "@/services/env";
import { useDisclosureData } from "@/services/useDisclosureData";
import { htmlify, interpolatePlaceholders } from "@/services/utils";
import type { ShareClassProfileEndpoint } from "@/types/endpoints/ShareClassProfileEndpoint";
import type { FundDetailLayoutComponentProps } from "@/types/fundDetailLayout";
import type { TranslationMap, V1DividendProfile } from "@/types/v1";

export interface FundPerformanceTablePanelProps extends FlexProps {
	heading?: string;
	tooltip?: string;
	sections?: {
		id: string;
		heading: string;
		data: DataTable;
		disclaimer?: string;
		tooltip?: string;
		asOf?: string;
	}[];
	translations?: TranslationMap;
	options?: {
		hasDividends?: boolean;
	};
	noPerformanceDisclaimer: string;
}

export const FxPerformanceTablePanel = (props: FundDetailLayoutComponentProps) => {
	const instanceConfig = useInstanceConfig();
	const [isFundDividendInfoDrawerLoading, setIsFundDividendInfoDrawerLoading] = useState<boolean>(false);
	const { dashboard, unitType, unitValue, unitCurrency, location, channel, language } = props;
	const theme = useTheme();
	const [isMobile] = useMediaQuery(`(max-width: ${theme?.breakpoints?.md || 320})`);

	const [isFundDividendInfoDrawerOpen, onFundDividendInfoDrawerOpen, onFundDividendInfoDrawerClose, fundDividendInfoDrawerData] =
		useDisclosureData<V1DividendProfile>();

	const [section, setSection] = useState(() => {
		return dashboard.fxPerformanceTablePanel?.sections ? dashboard.fxPerformanceTablePanel?.sections[0] : undefined;
	});

	const handleOnclick = useCallback(async () => {
		setIsFundDividendInfoDrawerLoading(true);
		onFundDividendInfoDrawerOpen();

		const response = await GET<ShareClassProfileEndpoint>(`${envVar("PUBLIC_ENV__API_HOST")}/api/v1/getShareClassProfileByExternalId`, {
			location,
			channel,
			language,
			shareClassExternalId: props.dashboard.shareClass.externalId
		});

		setIsFundDividendInfoDrawerLoading(false);

		if (response.isErr) {
			// TODO - Handle error
			return;
		}

		onFundDividendInfoDrawerOpen(response.body);
	}, [onFundDividendInfoDrawerOpen, location, channel, language, props.dashboard.shareClass.externalId]);

	if (!dashboard.fxPerformanceTablePanel) {
		return null;
	}

	const { heading, tooltip, sections, translations, options, noPerformanceDisclaimer } = dashboard.fxPerformanceTablePanel;

	if (noPerformanceDisclaimer) {
		return (
			<ContentBlock
				id="fxPerformanceTablePanel"
				data-testid="FxPerformanceTablePanel"
				heading={htmlify(heading)}
				tooltip={htmlify(tooltip)}
				tabIndex={-1}
			>
				<Flex padding="15px" bg="backgrounds.article">
					{htmlify(interpolatePlaceholders({ text: noPerformanceDisclaimer, instanceConfig }))}
				</Flex>
			</ContentBlock>
		);
	}

	return (
		<ContentBlock
			id="fxPerformanceTablePanel"
			data-testid="FxPerformanceTablePanel"
			heading={htmlify(heading)}
			tooltip={htmlify(tooltip)}
			tabIndex={-1}
		>
			{isMobile && (
				<>
					<AccessibleSelect
						aria-label="Select a group"
						id="performance-table"
						variant="brand"
						maxWidth="460px"
						minHeight="50px"
						fontWeight="bold"
						onChange={(e) => {
							const value = e.target.value;
							const newSection = sections?.[parseInt(value, 10)];

							setSection(newSection);
						}}
					>
						{sections?.map(({ heading }, headingIndex) => {
							return (
								<option key={headingIndex} value={headingIndex}>
									{htmlify(heading)}
								</option>
							);
						})}
					</AccessibleSelect>
					{section && (
						<div>
							{section.id === "priipsScenarios" ? <FxPerformancePriipsScenariosPanel {...props} /> : null}
							{section.id !== "priipsScenarios" ? (
								<PerfomanceTable
									data={section.data}
									unitCurrency={unitCurrency}
									unitType={unitType}
									unitValue={unitValue}
								/>
							) : null}

							{section.id == "distribution" && options?.hasDividends && (
								<Flex alignItems="center" justifyContent="center">
									<Button
										onClick={handleOnclick}
										variant="brand"
										marginTop="20px"
										marginBottom="10px"
										aria-haspopup="dialog"
									>
										{htmlify(props.dashboard?.translations?.viewDividendHistory)}
									</Button>
								</Flex>
							)}

							{Boolean(section.disclaimer) && <Disclaimer>{htmlify(section.disclaimer)}</Disclaimer>}
						</div>
					)}
				</>
			)}
			{!isMobile && (
				<Box>
					<AccessibleTabs variant="brandAndGrey" id="performance-table">
						<TabList overflow="visible" marginBottom={2.5}>
							{sections?.map(({ id, heading, tooltip: sectionTooltip }, headingIndex) => {
								return (
									<Tab minWidth={sections.length < 6 ? "20%" : "unset"} key={headingIndex} data-testid={`tab_${id}`}>
										<WithTooltip tooltip={sectionTooltip}>{htmlify(heading)}</WithTooltip>
									</Tab>
								);
							})}
						</TabList>
						<TabPanels>
							{sections?.map(({ id, data, disclaimer }, sectionIndex) => {
								return (
									<TabPanel
										key={sectionIndex}
										padding={0}
										data-testid={`tabPanel_${id}`}
										_focusVisible={{
											outline: "3px solid #A6CBED",
											outlineOffset: "0px",
											borderRadius: 6
										}}
									>
										{id === "priipsScenarios" ? <FxPerformancePriipsScenariosPanel {...props} /> : null}
										{id !== "priipsScenarios" ? (
											<PerfomanceTable
												data={data}
												unitCurrency={unitCurrency}
												unitType={unitType}
												unitValue={unitValue}
											/>
										) : null}

										{id == "distribution" && options?.hasDividends && (
											<Flex alignItems="center" justifyContent="center">
												<Button onClick={handleOnclick} variant="brand" marginTop="20px" marginBottom="10px">
													{htmlify(props.dashboard?.translations?.viewDividendHistory)}
												</Button>
											</Flex>
										)}

										{Boolean(disclaimer) && (
											<Disclaimer>
												{htmlify(interpolatePlaceholders({ text: disclaimer, instanceConfig }))}
											</Disclaimer>
										)}
									</TabPanel>
								);
							})}
						</TabPanels>
					</AccessibleTabs>
				</Box>
			)}

			<FxBestWorstPerformancePanel {...props} />

			{isFundDividendInfoDrawerOpen && (
				<FundDividendInfoDrawer
					isOpen={isFundDividendInfoDrawerOpen}
					isLoading={isFundDividendInfoDrawerLoading}
					onClose={onFundDividendInfoDrawerClose}
					item={fundDividendInfoDrawerData}
					restricted={true}
					translations={props.dashboard.translations}
				/>
			)}
		</ContentBlock>
	);
};
export const firstColumn = {
	backgroundColor: { base: "#F7F8FC", md: "inherit" }
};
export const mobileProps = {
	position: { base: "sticky", md: "unset" },
	left: { base: "0", md: "unset" },
	zIndex: { base: "docked", md: "unset" },
	whiteSpace: "break-spaces"
};

export const mobileAfter = {
	_after: {
		boxShadow: "inset 10px 0 8px -8px rgb(0 0 0 / 25%)",
		content: { base: `''`, md: "unset" },
		position: "absolute",
		top: 0,
		right: 0,
		bottom: "-1px",
		width: "30px",
		transform: "translateX(100%)"
	}
};
